<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { landingPageService } from '@/services/landingPage'
import { publicFilingHttpService } from '@/services/public-filing-http'
import { applySeo, restoreSeo } from '@/utils/seo'
import { SECTION_KEY } from '@/constants/landingSections'
import NavBar from '@/components/landing/NavBar.vue'
import HeroSection from '@/components/landing/HeroSection.vue'
import ProcessFlow from '@/components/landing/ProcessFlow.vue'
import FeatureCards from '@/components/landing/FeatureCards.vue'
import TaskBoardPreview from '@/components/landing/TaskBoardPreview.vue'
import DataStats from '@/components/landing/DataStats.vue'
import ScenarioDemo from '@/components/landing/ScenarioDemo.vue'
import Testimonials from '@/components/landing/Testimonials.vue'
import DocsAndNews from '@/components/landing/DocsAndNews.vue'
import CtaSection from '@/components/landing/CtaSection.vue'
import FooterSection from '@/components/landing/FooterSection.vue'

import { navItems } from '@/constants/landing'
import type { LandingPageData, PageSection } from '@/types/landingPage'
import type { PublicFilingInfo } from '@/types/system-config'

const data = ref<LandingPageData | null>(null)
const sections = ref<PageSection[]>([])
const loadError = ref('')
const filingInfo = ref<PublicFilingInfo | null>(null)

onMounted(async () => {
  try {
    // 修复配置审查#11：备案号走公开端点，匿名访客可见（原调管理端接口必 401/403 后被静默吞掉）
    const [pageData, sectionList, filing] = await Promise.all([
      landingPageService.getPublished(),
      landingPageService.getPublicSections(),
      publicFilingHttpService.getFilingInfo(),
    ])
    sections.value = sectionList
    data.value = pageData
    // 已发布的 SEO 配置注入 title/meta(此前管理端配置零消费,形同虚设)
    applySeo(pageData.seo)
    filingInfo.value = filing
  } catch (e) {
    console.error('Failed to load landing page:', e)
    loadError.value = '页面加载失败，请稍后重试'
  }
})

// 后端已过滤好，前端只需判断该区块是否在返回列表中
function isVisible(key: string): boolean {
  return sections.value.some(s => s.key === key)
}

// 离开公开首页时恢复首次应用前的 title/meta——SPA 共享 document,
// 不恢复会让首页 SEO 值泄漏到工作台/管理端等所有后续页面
onUnmounted(() => restoreSeo())
</script>

<template>
  <div class="home-page">
    <!-- 跳过导航链接 -->
    <a
      href="#hero"
      class="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:rounded focus:bg-indigo-600 focus:px-4 focus:py-2 focus:text-white"
    >
      跳到主内容
    </a>

    <div v-if="data">
      <NavBar :nav-items="navItems" />

    <HeroSection
      v-if="isVisible(SECTION_KEY.hero)"
      :headline="data.hero.headline"
      :subheadline="data.hero.subheadline"
      :primary-btn-text="data.hero.primaryBtnText"
      :primary-btn-link="data.hero.primaryBtnLink"
      :secondary-btn-text="data.hero.secondaryBtnText"
      :secondary-btn-link="data.hero.secondaryBtnLink"
      :trust-text="data.hero.trustText"
    />

    <ProcessFlow
      v-if="isVisible(SECTION_KEY.process)"
      :steps="data.processSteps ?? []"
    />

    <FeatureCards
      v-if="isVisible(SECTION_KEY.features)"
      :features="data.features ?? []"
    />

    <TaskBoardPreview
      v-if="isVisible(SECTION_KEY.board)"
      :columns="data.boardColumns ?? []"
    />

    <DataStats
      v-if="isVisible(SECTION_KEY.stats)"
      :stats="data.stats ?? []"
      :bottom-stat="data.primaryStat"
    />

    <ScenarioDemo
      v-if="isVisible(SECTION_KEY.scenarios)"
      :scenarios="data.scenarios ?? []"
    />

    <Testimonials
      v-if="isVisible(SECTION_KEY.testimonials)"
      :testimonials="data.testimonials ?? []"
    />

    <DocsAndNews
      v-if="isVisible(SECTION_KEY.docs) || isVisible(SECTION_KEY.news)"
      :docs="data.docEntries ?? []"
      :news="data.newsEntries ?? []"
    />

    <CtaSection
      v-if="isVisible(SECTION_KEY.cta)"
      :title="data.cta.title"
      :subtitle="data.cta.subtitle"
      :button-text="data.cta.buttonText"
      :button-link="data.cta.buttonLink"
      :mobile-button-text="data.cta.mobileButtonText"
    />

    <FooterSection
      v-if="isVisible(SECTION_KEY.footer)"
      :columns="data.footerGroups"
      :filing-info="filingInfo"
    />
    </div>
  </div>
</template>
